import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import { arrayOf, func, object, shape, string } from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

import styles from "./PlannerBlock.module.scss";

const RE_ASSESS_PAGES = 5;
const RE_ASSESS_LABEL = `Read ${RE_ASSESS_PAGES} More Pages And Re-Assess`;
const ANSWER_TYPE_OPTION = "option";
const ANSWER_TYPE_TEXT = "text";
export const DOCUMENT_TYPE_DIMENSION = "documentType";
// Sentinel optionId for the "read more pages and re-assess" action, distinct from
// a real candidate type choice. Exported so the gate can tell the two apart.
export const RE_ASSESS_OPTION_ID = "RE_ASSESS";
const SUBMIT_LABEL = "Submit Answers";

const findTypeQuestion = (questions) =>
  questions.find((q) => q.dimension === DOCUMENT_TYPE_DIMENSION);

const buildChipLabel = (typeQuestion) => {
  const top = typeQuestion?.options?.[0];
  if (typeof top?.score !== "number") {
    return null;
  }
  const pct = Math.round(top.score * 100);
  return `CONFIDENCE ${pct}% · BELOW 70% BAR`;
};

// When a question repeats across rounds (e.g. after a retry), the answer the user
// already gave may still be present in priorAnswers — prefill it instead of making
// them re-answer from scratch.
const findStoredAnswer = (answers, questionId) =>
  answers.find((a) => a.questionId === questionId);

const buildInitialSelectedOptions = (questions, answers) => {
  const initial = {};
  questions.forEach((q) => {
    const stored = findStoredAnswer(answers, q.id);
    if (stored?.answerType === ANSWER_TYPE_OPTION) {
      const option = q.options?.find((opt) => opt.id === stored.optionId);
      if (option) {
        initial[q.id] = option;
      }
    }
  });
  return initial;
};

const buildInitialTextAnswers = (questions, answers) => {
  const initial = {};
  questions.forEach((q) => {
    const stored = findStoredAnswer(answers, q.id);
    if (stored?.answerType === ANSWER_TYPE_TEXT) {
      initial[q.id] = stored.answer;
    }
  });
  return initial;
};

// Presentational renderer for a single clarification/planner block: the
// documentType question (confidence chip, scored candidate bars, per-option
// buttons, re-assess affordance) plus any other questions (radio options and/or
// a free-text field). Shared by OutlineClarificationModal and TypeReviewGate so
// the question/option/free-text rendering and answer-shape logic lives in one
// place. Purely presentational — the caller owns block/priorAnswers sourcing
// (Redux selectors, gate round state, etc.) and reacts to onSubmit.
const PlannerBlock = ({ block, priorAnswers = [], onSubmit }) => {
  const otherQuestions = useMemo(
    () =>
      (block.questions || []).filter(
        (q) => q.dimension !== DOCUMENT_TYPE_DIMENSION
      ),
    [block]
  );

  // Lazy initializers: computed once per mount (a fresh round mounts a fresh
  // PlannerBlock instance, keyed by blockId by the caller), seeding local answer
  // state from anything already accumulated for these question ids.
  const [textAnswers, setTextAnswers] = useState(() =>
    buildInitialTextAnswers(otherQuestions, priorAnswers)
  );
  const [selectedOptions, setSelectedOptions] = useState(() =>
    buildInitialSelectedOptions(otherQuestions, priorAnswers)
  );

  const typeQuestion = useMemo(
    () => findTypeQuestion(block.questions || []),
    [block]
  );
  const chipLabel = useMemo(() => buildChipLabel(typeQuestion), [typeQuestion]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // Serialize submissions and disable the controls while one is in flight: the
  // backend records each round write-once, so a double-click submits the same
  // round twice and the second hits "this review round has already been
  // answered". A successful submit often collapses (unmounts) the block, so the
  // finally is guarded by a mounted ref.
  const submit = async (answers) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(answers);
    } finally {
      if (mountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const onCandidate = (option) => {
    submit([
      {
        blockId: block.blockId,
        questionId: typeQuestion.id,
        question: typeQuestion.text,
        answerType: ANSWER_TYPE_OPTION,
        optionId: option.id,
        answer: option.label,
      },
    ]);
  };

  const onReAssess = () => {
    submit([
      {
        blockId: block.blockId,
        questionId: typeQuestion.id,
        question: typeQuestion.text,
        answerType: ANSWER_TYPE_OPTION,
        optionId: RE_ASSESS_OPTION_ID,
        answer: `Re-assess on ${RE_ASSESS_PAGES} more pages`,
      },
    ]);
  };

  const onSubmitOther = () => {
    const answers = otherQuestions
      .filter((q) => {
        const hasOption = selectedOptions[q.id] != null;
        const hasFreeText = textAnswers[q.id]?.trim();
        return hasOption || hasFreeText;
      })
      .map((q) => {
        const selectedOpt = selectedOptions[q.id];
        if (selectedOpt != null) {
          return {
            blockId: block.blockId,
            questionId: q.id,
            question: q.text,
            answerType: ANSWER_TYPE_OPTION,
            optionId: selectedOpt.id,
            answer: selectedOpt.label,
          };
        }
        return {
          blockId: block.blockId,
          questionId: q.id,
          question: q.text,
          answerType: ANSWER_TYPE_TEXT,
          optionId: null,
          answer: textAnswers[q.id].trim(),
        };
      });
    submit(answers);
  };

  return (
    <div className={styles.plannerBlock}>
      {typeQuestion && (
        <>
          {chipLabel && (
            <div className={styles.chipRow}>
              <span className={styles.chip}>{chipLabel}</span>
            </div>
          )}
          <div className={styles.question}>{typeQuestion.text}</div>
          <div className={styles.intro}>{block.intro}</div>

          <div className={styles.candidates}>
            {typeQuestion.options.map((opt, idx) => {
              const hasScore = typeof opt.score === "number";
              const pct = hasScore ? Math.round(opt.score * 100) : 0;
              return (
                <div key={opt.id}>
                  <div
                    className={
                      idx === 0 ? styles.candidateRow : styles.candidateRowMuted
                    }
                  >
                    <span>{opt.label}</span>
                    {hasScore && <span>{pct}%</span>}
                  </div>
                  {hasScore && (
                    <div className={styles.barTrack}>
                      {/* Runtime geometry: bar width is data-driven and cannot use a CSS token */}
                      <span
                        className={
                          idx === 0 ? styles.barFillTop : styles.barFill
                        }
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.actions}>
            {typeQuestion.options.map((opt, idx) => (
              <NewButton
                key={opt.id}
                className={styles.actionButton}
                variant={idx === 0 ? NewButtonVariant.l1 : NewButtonVariant.l2}
                onClick={() => onCandidate(opt)}
                disabled={isSubmitting}
              >
                {opt.label}
              </NewButton>
            ))}
          </div>

          <div className={styles.reassess}>
            <NewButton
              variant={NewButtonVariant.l3}
              onClick={onReAssess}
              disabled={isSubmitting}
            >
              {RE_ASSESS_LABEL}
            </NewButton>
          </div>
        </>
      )}

      {otherQuestions.map((q) => (
        <div key={q.id} className={styles.field}>
          <label className={styles.fieldLabel} htmlFor={`planner-${q.id}`}>
            {q.text}
          </label>
          {q.options?.map((opt) => (
            <label key={opt.id} className={styles.optionLabel}>
              <input
                type="radio"
                name={q.id}
                value={opt.id}
                checked={selectedOptions[q.id]?.id === opt.id}
                onChange={() =>
                  setSelectedOptions((prev) => ({ ...prev, [q.id]: opt }))
                }
              />
              {opt.label}
            </label>
          ))}
          {q.allowOther && (
            <textarea
              id={`planner-${q.id}`}
              className={styles.freeText}
              aria-label={q.text}
              value={textAnswers[q.id] || ""}
              onChange={(e) => {
                setSelectedOptions((prev) => {
                  const next = { ...prev };
                  delete next[q.id];
                  return next;
                });
                setTextAnswers((prev) => ({
                  ...prev,
                  [q.id]: e.target.value,
                }));
              }}
            />
          )}
        </div>
      ))}

      {otherQuestions.length > 0 && (
        <div className={styles.actions}>
          <NewButton
            className={styles.actionButton}
            variant={NewButtonVariant.l1}
            onClick={onSubmitOther}
            disabled={isSubmitting}
          >
            {SUBMIT_LABEL}
          </NewButton>
        </div>
      )}
    </div>
  );
};

PlannerBlock.propTypes = {
  block: shape({
    blockId: string,
    intro: string,
    questions: arrayOf(object),
  }).isRequired,
  priorAnswers: arrayOf(object),
  onSubmit: func.isRequired,
};

export default PlannerBlock;
